import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { DatabaseSortOrder, VirtualConnectionUpstreamDatabasesQueryVariables, VirtualConnectionUpstreamDatabasesQuery, DatabasesDisplayGridRowDataFragment, VirtualConnectionUpstreamDatabasesFilteredByTableQueryVariables, VirtualConnectionUpstreamDatabasesFilteredByTableQuery , VirtualConnectionUpstreamDatabasesFilteredByTableDocument , VirtualConnectionUpstreamDatabasesDocument } from '../../../../../gen-src/queries';
import {
  ConnectionListResult,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '../../../data-catalog-shared';
import { getRowDataWithMaxCountsForDatabase } from '../database/functions/DatabaseRowDataFunctions';
import { DatabasesDisplayGridRowData } from '../database/types/DatabasesDisplayGridRowData';
import { DatabasesDisplayGrid } from '../sharedDisplayGrid/DatabasesDisplayGrid';
import { DatabasesValidSortFields } from '../sorting/sortProviders/DatabasesGridSortProvider';


type QueryVariables = VirtualConnectionUpstreamDatabasesQueryVariables;
type QueryResults = VirtualConnectionUpstreamDatabasesQuery;
type QueryResultListNode = DatabasesDisplayGridRowDataFragment;
type GridRowData = DatabasesDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionUpstreamDatabasesFilteredByTableQueryVariables;
type QueryResultsWithFilter = VirtualConnectionUpstreamDatabasesFilteredByTableQuery;
type QueryResultListNodeWithFilter = DatabasesDisplayGridRowDataFragment;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.virtualConnections && resultData.virtualConnections[0] &&
    resultData.virtualConnections[0].upstreamDatabasesConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnections && resultData.virtualConnections[0];
  const virtualConnectionTables = virtualConnection && virtualConnection.tablesConnection && virtualConnection.tablesConnection.nodes;
  const database = virtualConnectionTables && virtualConnectionTables[0];
  return database ? database.upstreamDatabasesConnection : null;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.virtualConnections && resultData.virtualConnections[0];
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData | null {
  const virtualConnection = resultData && resultData.virtualConnections && resultData.virtualConnections[0];
  const virtualConnectionTables = virtualConnection && virtualConnection.tablesConnection && virtualConnection.tablesConnection.nodes;
  const database = virtualConnectionTables && virtualConnectionTables[0];
  return database;
}

function getRowDataForDatabase(
  table: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResults,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(table, selectMaxCountsResult(resultData));
}

function getRowDataForDatabaseForFilteredQuery(
  table: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(table, selectMaxCountsResultForFilteredQuery(resultData));
}

export type VirtualConnectionUpstreamDatabasesQueryGridProps = GridSortProps<DatabasesValidSortFields> & {
  readonly vizportalUrlId: string;
  readonly filteredTableId: string | null;
  readonly rowCount: number | null;
};

export function VirtualConnectionUpstreamDatabasesQueryGrid(props: VirtualConnectionUpstreamDatabasesQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyUpstreamDatabasesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredDatabasesLineageGridMessage();

  return props.filteredTableId ? (
    // Query with Selected Database as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionUpstreamDatabasesFilteredByTableDocument}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedTableId: props.filteredTableId,
        orderBy: props.orderBy as DatabaseSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDatabaseForFilteredQuery}
    >
      {({ rowStore }) =>
        <DatabasesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionUpstreamDatabasesDocument}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as DatabaseSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForDatabase}
    >
      {({ rowStore }) =>
        <DatabasesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  );
}
